import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, Phone, User, ArrowRight, ArrowLeft, Loader2, Clock, CheckCircle, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { AuthError } from '../components/AuthError';
import { authErrorMessage, companyCodeInput } from '../utils/authForm';

const EmployeeSignupPage = () => {
  const navigate = useNavigate();
  const { signupEmployee } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [formData, setFormData] = useState({ companyCode: '', name: '', phone: '' });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setErrorMessage('');
    if (!formData.companyCode || !formData.name.trim() || !formData.phone.trim()) {
      setErrorMessage('Please fill all fields'); return;
    }
    if (!/^[A-Z]{2}\d{4}$/.test(companyCodeInput(formData.companyCode))) {
      setErrorMessage('Enter the full 6-character company code, for example JO2026.'); return;
    }
    setLoading(true);
    try {
      await signupEmployee(formData.companyCode, formData.phone, formData.name);
      setSuccess(true);
      toast.success('Registration submitted!');
    } catch (error) {
      setErrorMessage(authErrorMessage(error, 'Registration failed'));
    } finally { setLoading(false); }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // SUCCESS STATE
  // ══════════════════════════════════════════════════════════════════════════
  if (success) {
    return (
      <div data-testid="signup-success" style={{
        minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: '24px 20px',
        fontFamily: "'Manrope', 'Inter', sans-serif",
        background: 'linear-gradient(165deg, #faf5ff 0%, #f3e8ff 30%, #e9d5ff 60%, #f5f3ff 100%)',
        position: 'relative', overflowX: 'hidden', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 70%)', top: -100, right: 0 }} />
        </div>
        <div style={{
          width: 88, height: 88, borderRadius: 24, marginBottom: 24,
          background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 16px 48px rgba(139,92,246,0.4)', position: 'relative', zIndex: 1,
        }}>
          <Clock style={{ width: 44, height: 44, color: '#fff' }} />
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 900, textAlign: 'center', margin: '0 0 8px', color: '#0f172a', position: 'relative', zIndex: 1 }}>
          Request Submitted! 👷
        </h1>
        <p style={{ fontSize: 13, color: '#64748b', textAlign: 'center', margin: '0 0 28px', maxWidth: 280, lineHeight: 1.5, position: 'relative', zIndex: 1 }}>
          Your admin will review and approve your request soon.
        </p>
        <div style={{
          width: '100%', maxWidth: 320, background: 'rgba(255,255,255,0.9)', borderRadius: 20, padding: '20px', textAlign: 'left',
          border: '1px solid rgba(139,92,246,0.2)', boxShadow: '0 8px 32px rgba(139,92,246,0.12)',
          backdropFilter: 'blur(12px)', marginBottom: 24, position: 'relative', zIndex: 1,
        }}>
          {[{ num: '1', text: 'Admin reviews request', sub: 'Usually few hours' },
            { num: '2', text: 'Designation assigned', sub: 'Based on your role' },
            { num: '3', text: 'You get access', sub: 'Login with phone' }].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: i < 2 ? 14 : 0 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                background: `linear-gradient(135deg, ${['#8b5cf6', '#a855f7', '#c084fc'][i]}, ${['#a855f7', '#c084fc', '#d8b4fe'][i]})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900, color: '#fff',
              }}>{item.num}</div>
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', margin: 0 }}>{item.text}</p>
                <p style={{ fontSize: 10, color: '#94a3b8', margin: 0 }}>{item.sub}</p>
              </div>
            </div>
          ))}
        </div>
        <button data-testid="employee-signup-back-to-login" onClick={() => navigate('/login')} style={{
          width: '100%', maxWidth: 320, height: 54, borderRadius: 14, border: 'none',
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          boxShadow: '0 8px 24px rgba(139,92,246,0.35)', position: 'relative', zIndex: 1,
        }}>
          Back to Login <ArrowRight style={{ width: 18, height: 18 }} />
        </button>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE LAYOUT
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <div data-testid="employee-signup-page" style={{
        minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: '24px 20px',
        fontFamily: "'Manrope', 'Inter', sans-serif",
        background: 'linear-gradient(165deg, #f0fdfa 0%, #ecfdf5 30%, #ccfbf1 60%, #f0fdf4 100%)',
        position: 'relative', overflowX: 'hidden', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(20,184,166,0.15) 0%, transparent 70%)',
            top: -100, right: 0, animation: 'float 8s ease-in-out infinite' }} />
        </div>
        <Link data-testid="employee-signup-back-link" to="/login" style={{
          position: 'absolute', top: 20, left: 20, width: 40, height: 40, borderRadius: 12,
          background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(13,148,136,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', zIndex: 10,
        }}>
          <ArrowLeft style={{ width: 20, height: 20, color: '#0d9488' }} />
        </Link>
        <div style={{ textAlign: 'center', marginBottom: 28, position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>👷</div>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: '#0f172a', margin: '0 0 6px' }}>Join Team</h1>
          <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Have an invite code? Join now</p>
        </div>
        <form onSubmit={handleSubmit} data-testid="employee-signup-form" style={{
          width: '100%', maxWidth: 340, background: 'rgba(255,255,255,0.9)', borderRadius: 24, padding: '28px 24px',
          border: '1px solid rgba(13,148,136,0.15)', boxShadow: '0 8px 32px rgba(13,148,136,0.1)',
          backdropFilter: 'blur(20px)', position: 'relative', zIndex: 1,
        }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#0d9488', display: 'block', marginBottom: 6 }}>Company Code</label>
            <div style={{ position: 'relative' }}>
              <Building2 style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                width: 18, height: 18, color: focusedField === 'code' ? '#0d9488' : '#94a3b8', transition: 'color 0.2s' }} />
              <Input type="text" placeholder="e.g. JO2026" value={formData.companyCode} data-testid="employee-signup-company-code" autoCapitalize="characters"
                onChange={e => setFormData({ ...formData, companyCode: companyCodeInput(e.target.value) })}
                onPaste={e => { e.preventDefault(); setFormData({ ...formData, companyCode: companyCodeInput(e.clipboardData.getData('text')) }); }}
                onFocus={() => setFocusedField('code')} onBlur={() => setFocusedField(null)}
                style={{
                  width: '100%', height: 52, paddingLeft: 46, fontSize: 18, fontWeight: 700, letterSpacing: '0.2em', textAlign: 'center',
                  background: focusedField === 'code' ? '#fff' : '#f0fdfa',
                  border: focusedField === 'code' ? '2px solid #0d9488' : '2px solid #ccfbf1',
                  borderRadius: 14, color: '#0f172a', outline: 'none', transition: 'all 0.2s',
                }} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 6 }}>Your Name</label>
            <div style={{ position: 'relative' }}>
              <User style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                width: 18, height: 18, color: focusedField === 'name' ? '#7c3aed' : '#94a3b8', transition: 'color 0.2s' }} />
              <Input type="text" placeholder="Your full name" value={formData.name} data-testid="employee-signup-name" autoComplete="name"
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                onFocus={() => setFocusedField('name')} onBlur={() => setFocusedField(null)}
                style={{
                  width: '100%', height: 52, paddingLeft: 46, fontSize: 15, fontWeight: 500,
                  background: focusedField === 'name' ? '#fff' : '#f8fafc',
                  border: focusedField === 'name' ? '2px solid #7c3aed' : '2px solid #e2e8f0',
                  borderRadius: 14, color: '#0f172a', outline: 'none', transition: 'all 0.2s',
                }} />
            </div>
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 6 }}>Phone Number</label>
            <div style={{ position: 'relative' }}>
              <Phone style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                width: 18, height: 18, color: focusedField === 'phone' ? '#ec4899' : '#94a3b8', transition: 'color 0.2s' }} />
              <Input type="tel" placeholder="Your phone number" value={formData.phone} data-testid="employee-signup-phone" autoComplete="tel"
                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                onFocus={() => setFocusedField('phone')} onBlur={() => setFocusedField(null)}
                style={{
                  width: '100%', height: 52, paddingLeft: 46, fontSize: 15, fontWeight: 500,
                  background: focusedField === 'phone' ? '#fff' : '#f8fafc',
                  border: focusedField === 'phone' ? '2px solid #ec4899' : '2px solid #e2e8f0',
                  borderRadius: 14, color: '#0f172a', outline: 'none', transition: 'all 0.2s',
                }} />
            </div>
          </div>
          <AuthError message={errorMessage} testId="employee-signup-error" />
          <button type="submit" disabled={loading} data-testid="employee-signup-submit"
            style={{
              width: '100%', height: 54, borderRadius: 14, border: 'none',
              background: loading ? 'rgba(13,148,136,0.5)' : 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)',
              color: '#fff', fontSize: 15, fontWeight: 800, cursor: loading ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: loading ? 'none' : '0 8px 24px rgba(13,148,136,0.35)', transition: 'all 0.2s',
            }}>
            {loading ? <Loader2 style={{ width: 20, height: 20, animation: 'spin 1s linear infinite' }} />
              : <>Join Team <ArrowRight style={{ width: 18, height: 18 }} /></>}
          </button>
        </form>
        <p style={{ fontSize: 12, color: '#64748b', marginTop: 20, textAlign: 'center', position: 'relative', zIndex: 1 }}>
          Want to create your own company? <Link data-testid="employee-signup-create-company-link" to="/signup/company" style={{ color: '#0d9488', fontWeight: 700, textDecoration: 'none' }}>Create Company</Link>
        </p>
        <style>{`
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
          input::placeholder { color: #94a3b8 !important; }
        `}</style>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP LAYOUT (2-Column)
  // ══════════════════════════════════════════════════════════════════════════
  const BG = 'linear-gradient(150deg,#f0fdfa 0%,#fdf4ff 35%,#fff7ed 65%,#f0fdf9 100%)';
  const fields = [
    { id: 'companyCode', icon: Building2, label: 'Company Code', placeholder: "e.g. JO2026", key: 'companyCode', color: '#0d9488' },
    { id: 'name', icon: User, label: 'Your Name', placeholder: "Your full name", key: 'name', color: '#7c3aed' },
    { id: 'phone', icon: Phone, label: 'Phone Number', placeholder: "Your phone number", key: 'phone', color: '#ec4899', type: 'tel' },
  ];

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: "'Manrope','Inter',sans-serif", background: BG, position: 'relative', overflowX: 'hidden', overflowY: 'auto' }}>
      {/* Jali */}
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', inset: 0, opacity: 0.04 }}>
        <defs><pattern id="jaliE" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
          <circle cx="30" cy="30" r="12" fill="none" stroke="#0d9488" strokeWidth="0.7"/>
        </pattern></defs>
        <rect width="100%" height="100%" fill="url(#jaliE)"/>
      </svg>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', width: 600, height: 600, borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(13,148,136,0.09) 0%,transparent 65%)', top: -200, left: -150 }}/>
        <div style={{ position: 'absolute', width: 500, height: 500, borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(124,58,237,0.07) 0%,transparent 65%)', top: -80, right: 0 }}/>
      </div>

      {/* Left — Brand */}
      <div style={{ flex: '0 0 44%', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '56px 52px', position: 'relative', zIndex: 1 }}>
        <Link data-testid="employee-signup-brand-link" to="/login" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 48 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,#0d9488,#7c3aed,#ec4899)', padding: 3, boxShadow: '0 6px 20px rgba(13,148,136,0.3)' }}>
            <img src="/sitepe-logo.png" alt="SitePe" style={{ width: '100%', height: '100%', borderRadius: 9, display: 'block' }} />
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, letterSpacing: '-0.02em' }}>
              <span style={{ color: '#0f172a' }}>site</span>
              <span style={{ background: 'linear-gradient(135deg,#0d9488,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>pe</span>
            </div>
            <div style={{ fontSize: 8, color: 'rgba(13,148,136,0.6)', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase' }}>Construction ERP</div>
          </div>
        </Link>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '4px 12px', borderRadius: 20,
          background: 'linear-gradient(135deg,rgba(13,148,136,0.08),rgba(124,58,237,0.06))',
          border: '1px solid rgba(13,148,136,0.2)', marginBottom: 16, width: 'fit-content' }}>
          <span style={{ fontSize: 11 }}>👷</span>
          <span style={{ fontSize: 9, fontWeight: 800, background: 'linear-gradient(135deg,#0d9488,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '0.1em' }}>JOIN YOUR TEAM</span>
        </div>
        <h1 style={{ fontSize: 40, fontWeight: 900, color: '#0f172a', margin: '0 0 14px', lineHeight: 1.1, letterSpacing: '-0.03em' }}>
          Join Your<br/>
          <span style={{ background: 'linear-gradient(135deg,#0d9488,#7c3aed,#ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Construction Team</span>
        </h1>
        <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 32px', lineHeight: 1.7, maxWidth: 340 }}>
          Got an invite code from your company? Join in seconds and start managing your work from day one.
        </p>
        {[['1', 'Enter company code', 'Get this from your admin'],
          ['2', 'Fill your details', 'Name & phone number'],
          ['3', 'Wait for approval', 'Admin will assign your role']].map(([n, t, s], i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 26, height: 26, borderRadius: 8, flexShrink: 0,
              background: `linear-gradient(135deg,${['#0d9488', '#7c3aed', '#ec4899'][i]},${['#14b8a6', '#a855f7', '#f472b6'][i]})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: '#fff' }}>{n}</div>
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', margin: 0 }}>{t}</p>
              <p style={{ fontSize: 10, color: '#94a3b8', margin: 0 }}>{s}</p>
            </div>
          </div>
        ))}
        <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 24 }}>
          Want to create your own company? <Link data-testid="employee-signup-create-company-link" to="/signup/company" style={{ background: 'linear-gradient(135deg,#0d9488,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: 700, textDecoration: 'none' }}>Create Company →</Link>
        </p>
      </div>

      {/* Right — Form */}
      <div style={{ flex: '0 0 56%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 56px', position: 'relative', zIndex: 1 }}>
        <div style={{ position: 'absolute', left: 0, top: '10%', height: '80%', width: 1,
          background: 'linear-gradient(to bottom,transparent,rgba(13,148,136,0.2),rgba(124,58,237,0.15),transparent)' }}/>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div style={{ marginBottom: 24, textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
              <div style={{ height: 1, width: 28, background: 'linear-gradient(to right,transparent,rgba(13,148,136,0.35))' }}/>
              <span style={{ fontSize: 13, background: 'linear-gradient(135deg,#0d9488,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>✦</span>
              <div style={{ height: 1, width: 28, background: 'linear-gradient(to left,transparent,rgba(124,58,237,0.3))' }}/>
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 900, color: '#0f172a', margin: '0 0 5px', letterSpacing: '-0.02em' }}>Join Your Team</h2>
            <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>Enter your invite code to get started</p>
          </div>
          <form onSubmit={handleSubmit} data-testid="employee-signup-form" style={{ background: 'rgba(255,255,255,0.75)', borderRadius: 22, padding: '28px 24px',
            border: '1.5px solid rgba(13,148,136,0.12)', backdropFilter: 'blur(20px)',
            boxShadow: '0 4px 6px rgba(0,0,0,0.03),0 20px 48px rgba(13,148,136,0.08)' }}>
            {fields.map(({ id, icon: Icon, label, placeholder, key, color, type }) => (
              <div key={id} style={{ marginBottom: 16 }}>
                <Label style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', display: 'block', marginBottom: 7, color: `${color}99` }}>{label}</Label>
                <div style={{ position: 'relative' }}>
                  <Icon style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 15, height: 15, transition: 'color 0.2s', color: focusedField === id ? color : '#cbd5e1' }}/>
                  <Input type={type || 'text'} placeholder={placeholder} value={formData[key]} data-testid={`employee-signup-${key === 'companyCode' ? 'company-code' : key}`}
                    onChange={e => setFormData({ ...formData, [key]: key === 'companyCode' ? companyCodeInput(e.target.value) : e.target.value })}
                    onPaste={key === 'companyCode' ? e => { e.preventDefault(); setFormData({ ...formData, companyCode: companyCodeInput(e.clipboardData.getData('text')) }); } : undefined}
                    onFocus={() => setFocusedField(id)} onBlur={() => setFocusedField(null)}
                    style={{
                      width: '100%', height: 44, paddingLeft: 38, fontSize: key === 'companyCode' ? 20 : 14,
                      fontWeight: key === 'companyCode' ? 700 : 500, letterSpacing: key === 'companyCode' ? '0.25em' : 'normal',
                      background: '#fff', border: focusedField === id ? `1.5px solid ${color}` : '1.5px solid #e2e8f0',
                      borderRadius: 11, color: '#0f172a', outline: 'none',
                      boxShadow: focusedField === id ? `0 0 0 3px ${color}18` : 'none', transition: 'all 0.2s',
                    }}/>
                </div>
              </div>
            ))}
            <AuthError message={errorMessage} testId="employee-signup-error" />
            <button type="submit" disabled={loading} data-testid="employee-signup-submit"
              style={{
                width: '100%', height: 48, borderRadius: 13, border: 'none', marginTop: 8,
                background: loading ? 'rgba(13,148,136,0.5)' : 'linear-gradient(135deg,#0d9488 0%,#7c3aed 50%,#ec4899 100%)',
                color: '#fff', fontSize: 14, fontWeight: 800, cursor: loading ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: loading ? 'none' : '0 8px 24px rgba(13,148,136,0.25)', transition: 'all 0.2s',
              }}>
              {loading ? <Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                : <>Join Team <ArrowRight style={{ width: 14, height: 14 }} /></>}
            </button>
          </form>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 16 }}>
            <Shield style={{ width: 12, height: 12, color: '#0d9488', opacity: 0.5 }} />
            <span style={{ fontSize: 10, color: '#94a3b8' }}>Your data is secure and encrypted</span>
          </div>
        </div>
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default EmployeeSignupPage;
